/**
 * Pure derivation for the portfolio panel's positions table
 * (DIRECTION.md §1.7) — reads the latest `portfolio.snapshot` event's
 * `positions[]` (real fixture shape verified against
 * `fixtures/male-equity-founder-0001/events/*.json`: `{basis, mark_price,
 * mark_stale, quantity, symbol, mark_knowledge_time}`), the same event
 * `src/panels/summaryStrip.ts` already reads for the positions/orders
 * count, so a caller never has to fetch anything new.
 *
 * `basis` is the position's TOTAL cost basis (`organism.environment.accounts.
 * PositionState.basis`: 21 AAPL bought for $6,940.49 carries basis "6940.49"),
 * not a per-unit price. OQA (2026-10-02, live male book): this module used to
 * read it as the per-unit "avg cost" and computed `(mark - basis) * qty`, so the
 * table showed AAPL's avg cost as $6,940.49 and its unrealized P&L as
 * -$138,824.70 for a real -$14.90. Now: avg cost = basis / quantity and
 * unrealized P&L = mark * quantity - basis — the ledger's own `marked_value -
 * basis`, not a statistical model, so it isn't given a "model" label, but its
 * tooltip still states the formula for full provenance. A position
 * missing `mark_price`/`basis`/`quantity` (never expected, but never
 * trusted blindly either) shows "–" for that cell rather than a guessed
 * number.
 *
 * There is currently no per-order detail (symbol/side/limit price) in this
 * app's event contract — only the aggregate `open_orders` count
 * (`portfolio.snapshot.payload.open_orders`) and, separately, settled
 * fills (`env_fill_settled`, already rendered by the activity feed). This
 * module's `latestOpenOrdersCount` surfaces that honest count; the caller
 * renders a note explaining why there's a count but no table, rather than
 * fabricating order rows.
 */

import type { LogEvent } from "../types/events";
import { latestByKind } from "../brain/sessionStatus";

export interface PositionRow {
  readonly symbol: string;
  readonly quantity: number | null;
  /** The published quantity string, verbatim (fractional crypto on the 1e-9
   * grid must not round-trip through a float for display). */
  readonly quantityText: string | null;
  /** TOTAL cost basis of the position (not per unit). */
  readonly basis: number | null;
  /** `basis / quantity` — the average cost per unit. */
  readonly avgCost: number | null;
  readonly markPrice: number | null;
  /** The published mark string, verbatim. */
  readonly markText: string | null;
  readonly markStale: boolean;
  readonly unrealizedPnl: number | null;
}

function num(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function text(value: unknown): string | null {
  if (typeof value === "string" && value.length > 0) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function buildPositionRows(events: readonly LogEvent[]): readonly PositionRow[] {
  const latest = latestByKind(events, "portfolio.snapshot");
  const raw = latest?.payload?.["positions"];
  if (!Array.isArray(raw)) return [];

  return raw.filter(isRecord).map((entry): PositionRow => {
    const symbol = typeof entry["symbol"] === "string" ? entry["symbol"] : "–";
    const quantity = num(entry["quantity"]);
    const quantityText = text(entry["quantity"]);
    const basis = num(entry["basis"]);
    const markPrice = num(entry["mark_price"]);
    const markText = text(entry["mark_price"]);
    const markStale = entry["mark_stale"] === true;
    const avgCost = quantity !== null && quantity !== 0 && basis !== null ? basis / quantity : null;
    const unrealizedPnl =
      quantity !== null && basis !== null && markPrice !== null
        ? markPrice * quantity - basis
        : null;
    return {
      symbol,
      quantity,
      quantityText,
      basis,
      avgCost,
      markPrice,
      markText,
      markStale,
      unrealizedPnl,
    };
  });
}

export function latestOpenOrdersCount(events: readonly LogEvent[]): number | null {
  const latest = latestByKind(events, "portfolio.snapshot");
  const value = latest?.payload?.["open_orders"];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
